/* eslint-disable consistent-return */
import * as React from 'react';
import styled from '@emotion/styled';
import Pagination from '@mui/material/Pagination';
import Checkbox from '@mui/material/Checkbox';
import axios from 'axios';
import { FormControlLabel } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { SearchItem, FinalDataReturn, PractitionerOrOrganization, currentLocationPos } from '../../../../interfaces/interfaces';
import NumberOfResults from './NumberOfResults';
import PractitionerTable from './PractitionerTable';
import MapSection from '../MapSection';
import createURL, { PAGE_COUNT } from '../../../../utils/URLcreators';
import newEntryPractitionerAndOrganization from '../../../../utils/adapters/adaptPractitionersAndOrganizations';
import { CheckboxEntityTypeObj, FilterEntityInterface } from '../Filter/Filter.interface';
import urlHandler from '../../../../utils/urlHandler';
import { dataDistanceFn, sortByDistanceFn } from '../../../../utils/sort/sort';

const StyledContainerMap = styled.div`
  position: relative;
  display: flex;
  margin-top: 1rem;
  width: 100%;
  flex: 1;
`;

const StyledContainerResults = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  width: 25rem;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
`;

const PractitionersList = ({
  value,
  filterData,
  nameOfSearch,
}: {
  value: SearchItem | null;
  filterData: FilterEntityInterface;
  nameOfSearch: string;
}) => {
  const [page, setPage] = React.useState<number | undefined>(1);
  const [practitionersArr, setPractitionersArr] = React.useState<FinalDataReturn>({
    data: new Map(),
    location: new Map(),
    role: new Map(),
    total: 0,
  });
  const [resourceArr, setResourceArr] = React.useState<Map<string, PractitionerOrOrganization>>(new Map());
  const [timeLoad, setTimeLoad] = React.useState<number>(0);
  const [practitionerOrOrg, setPractitionerOrOrg] = React.useState<PractitionerOrOrganization | undefined>(undefined);
  const [currentLocation, setCurrentLocation] = React.useState<currentLocationPos | null>(null);
  const [checkedSort, setCheckedSort] = React.useState(false);
  const didMount = React.useRef(false);
  const { getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = React.useState(false);

  const { distance, provinces, languages, gender, country, catchmentArea, specialty, near, name } = filterData;
  const { embedding: _embedding } = value ?? {};

  const handleFilterOutComeValues = React.useCallback((valuesArr: CheckboxEntityTypeObj[]) => {
    return valuesArr?.reduce((acc, item) => [...acc, item.code], [] as string[]);
  }, []);
  const queryStringSchema = React.useMemo(() => {
    return {
      _embedding,
      near: near?.join(', '),
      name: name?.join(', '),
      radius: Number(distance?.join(', ')) === 0 ? null : Number(distance?.join(', ')),
      specialty: handleFilterOutComeValues(specialty),
      languages: handleFilterOutComeValues(languages),
      gender: gender?.join(', ') === 'All' ? null : gender?.join(', '),
      province: handleFilterOutComeValues(provinces),
      country: country.join(', ') === 'All' ? null : country.join(', '),
      page,
      catchment_area: handleFilterOutComeValues(catchmentArea),
    };
  }, [_embedding, near, name, distance, handleFilterOutComeValues, specialty, languages, gender, provinces, country, page, catchmentArea]);

  const searchURL = React.useMemo(() => createURL(queryStringSchema), [queryStringSchema]);

  React.useEffect(() => {
    setPage(1);
  }, [value, filterData]);

  React.useEffect(() => {
    const controller = new AbortController();
    if (didMount.current) {
      const loadCity = async () => {
        // creating urls for two endpoints ('/v1/Practitioner', '/v1/Organization') depending on the condition of query string
        setIsLoading(true);
        const urls = urlHandler(searchURL, nameOfSearch)();

        const accessToken = await getAccessTokenSilently();

        const getFnForPromise = (uri: string[]) =>
          uri.map((item) => {
            const authUrl = item.includes('?') ? `${item}&jwt=${accessToken}` : `${item}?jwt=${accessToken}`;
            return axios.get(authUrl, {
              signal: controller.signal,
            });
          });

        const start: number = Date.now();
        const res = await Promise.all(getFnForPromise(urls)).then((results) => {
          return results
            .filter(({ data }) => {
              return data?.total;
            })
            .map((item) => {
              const { data } = item;
              return {
                data: data?.entry
                  ?.map((i: fhir4.BundleEntry) => i.resource)
                  .filter((i: fhir4.Practitioner | fhir4.Organization | fhir4.PractitionerRole | fhir4.Location) => !!i) as (
                  | fhir4.Practitioner
                  | fhir4.Organization
                  | fhir4.PractitionerRole
                  | fhir4.Location
                )[],
                total: data?.total as number | undefined,
              };
            });
        });

        const newEntry = newEntryPractitionerAndOrganization({ res, nameOfSearch }, currentLocation);
        setPractitionersArr(newEntry);
        setResourceArr(checkedSort ? sortByDistanceFn(newEntry).data : newEntry.data);

        const end: number = Date.now();
        setTimeLoad(end - start);
        setIsLoading(false);
      };
      loadCity().catch((e) => console.error(e.message)); // eslint-disable-line no-console
    } else {
      didMount.current = true;
    }
    return () => {
      setTimeLoad(0);
      setPractitionerOrOrg(undefined);
      controller.abort();
    };
  }, [value, searchURL, filterData, currentLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const isCurrentPosition = sessionStorage.getItem('currentPosition');
    if (isCurrentPosition) {
      setCurrentLocation(JSON.parse(isCurrentPosition));
    }
    return () => setCurrentLocation(null);
  }, []);

  // useEffect for sorting by distance
  React.useEffect(() => {
    if (checkedSort) {
      setResourceArr(dataDistanceFn(practitionersArr).data);
    } else {
      setResourceArr(practitionersArr.data);
    }
  }, [checkedSort]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalNumberOfResults: number | undefined = practitionersArr?.total;
  // if (PractitionersAndOrg?.error) return <pre>{JSON.stringify(PractitionersAndOrg.error, null, 2)}</pre>;

  const handleChangePage = React.useCallback((event: React.ChangeEvent<unknown>, val: number): void => setPage(val), []);
  const handleChangeSortStatus = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => setCheckedSort(e.target.checked), []);
  return (
    <>
      <StyledContainerResults>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <NumberOfResults totalValue={totalNumberOfResults} durationSearch={totalNumberOfResults !== 0 ? timeLoad : 0} />
        )}
        {currentLocation && (
          <FormControlLabel
            sx={{ paddingLeft: '1rem' }}
            control={
              <Checkbox
                checked={checkedSort}
                onChange={handleChangeSortStatus}
                sx={{
                  margin: '0 0.2rem 0 1rem',
                  padding: '0',
                }}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label="Sort by distance"
          />
        )}
      </StyledContainerResults>

      <StyledContainerMap>
        <MapSection resourceArr={resourceArr} markerPractArr={practitionerOrOrg} setCurrentLocation={setCurrentLocation}>
          {!isLoading && !!resourceArr?.size && didMount.current && (
            <StyledList>
              <PractitionerTable resourceArr={resourceArr} setPractitionerOrOrg={setPractitionerOrOrg} practitionerOrOrg={practitionerOrOrg}>
                <Pagination
                  size="small"
                  sx={{ padding: '1rem', bgcolor: '#ffffff', borderColor: '#ffffff' }}
                  onChange={handleChangePage}
                  page={page}
                  count={Math.ceil((totalNumberOfResults ?? 0) / PAGE_COUNT)}
                  variant="outlined"
                  shape="rounded"
                />
              </PractitionerTable>
            </StyledList>
          )}
        </MapSection>
      </StyledContainerMap>
    </>
  );
};

export default PractitionersList;
